import { useCallback, useRef, useState, FocusEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Automation2Service from 'services/Automation2Service';

import BasicModalWithConfirmation from '__components/Modals/presets/BasicModalWithConfirmation';
import TextInput from '__components/Inputs/TextInput';

import { useAutomationsDispatch } from '../store/react';
import { actions } from '../store';

import { toastVariant } from 'components/ToastProvider';

import { StyledNotice } from './styled';
import { AutomationLike } from '../types';

import styled from '@emotion/styled';

import { validatePriceNumber } from 'components/Inputs/TextInput/NumericValidation/helpers';
import { useFlashTransition } from '__hooks/useFlashState';
import { layers } from '__app/spacing';

const StyledTextInput = styled(TextInput)`
  margin-bottom: 20px;
`;

type ThrottleAuntomationModal = {
  automation: AutomationLike;
  onConfirm: (automation: AutomationLike) => void;
  onHide: () => void;
  show: boolean;
};

const MIN_VALUE = 0.1;
const MAX_VALUE = 172800;

const isValid = (value: number) => value >= MIN_VALUE && value <= MAX_VALUE;

const PopperConfig = {
  popperConfig: {
    modifiers: {
      preventOverflow: { enabled: false },
    },
  },
};
export const ThrottleAuntomationModal = ({
  automation,
  onConfirm,
  onHide,
  show,
}: ThrottleAuntomationModal) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();

  const [value, setValue] = useState(automation.triggerThrottleSeconds);
  const originalValue = useRef<number | null>(null);

  const [message, showMessage, flash] = useFlashTransition();
  const isDisabled = !isValid(value);

  if (originalValue.current === null && automation.triggerThrottleSeconds) {
    originalValue.current = automation.triggerThrottleSeconds;
    setValue(automation.triggerThrottleSeconds);
  }

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const valueString = parseFloat(value).toString();
      if (!isValid(parseFloat(value)) || value !== valueString) {
        flash(
          t(`Value must be between {{min}} and {{max}}.`, {
            min: MIN_VALUE,
            max: MAX_VALUE,
          })
        );
      }
    },
    [flash, t]
  );

  const handleConfirm = useCallback(async () => {
    try {
      const updatedAutomation = await Automation2Service.update({
        id: automation.id,
        triggerThrottleSeconds: value,
        lastRevision: automation.revision,
      });
      dispatch(
        actions.toast({
          variant: toastVariant.SUCCESS,
          message: t('Automation Throttling Time Updated'),
        })
      );
      onConfirm(updatedAutomation);
    } catch (error) {
      dispatch(
        actions.toast({
          variant: toastVariant.FAILURE,
          message: t('Automation Throttling Time Update Failed'),
        })
      );
    }
  }, [automation.id, automation.revision, dispatch, onConfirm, t, value]);

  const validation = useMemo(() => {
    return {
      message,
      showMessage,
      ...PopperConfig,
      ...validatePriceNumber(t),
      zIndexOverride: layers.modals(0, 2),
    };
  }, [message, showMessage, t]);

  return (
    <BasicModalWithConfirmation
      heading={t('Throttle Triggers')}
      onConfirm={handleConfirm}
      onHide={onHide}
      show={show}
      dirty={value !== originalValue.current}
      hasOneBtnSubmit
      disabled={isDisabled}
    >
      <StyledNotice>
        {t(
          'Subsequent automation triggers for a single record will be ignored for the specified time period.'
        )}
      </StyledNotice>
      <StyledTextInput
        value={value}
        onChange={setValue}
        onBlur={handleBlur}
        label={t('Seconds to Wait')}
        placeholder={t('Enter number')}
        required
        validate={validation}
      />
    </BasicModalWithConfirmation>
  );
};
